// Normalize an axios / fetch / Error into a human-readable string.
//
// Why: FastAPI (Pydantic v2) returns 422 validation errors with
// `response.data.detail` shaped as an array of
// `{type, loc, msg, input, ctx}` objects. ~140 sites in this codebase
// pass `error.response.data.detail` straight into Alert / Snackbar
// children. When that detail is an array, React throws #31 ("Objects
// are not valid as a React child") and the page crashes.
//
// This helper accepts any error shape we've seen in production and
// returns a string suitable for display. Use it at every site where
// an error message is rendered.
export function formatApiError(error, fallback = 'Something went wrong.') {
  // String error already
  if (typeof error === 'string') return error;
  if (!error) return fallback;

  // Axios error object — pull the response body
  const data = error?.response?.data;
  const detail = data?.detail ?? data?.message ?? data?.error;

  if (typeof detail === 'string' && detail.length > 0) return detail;

  // Pydantic v2 validation error: detail is an array of
  // {type, loc, msg, input, ctx} entries. Render each one as
  // "<field>: <msg>" and join them.
  if (Array.isArray(detail)) {
    const parts = detail.map((d) => {
      if (typeof d === 'string') return d;
      if (d?.msg) {
        // loc is typically ['body', 'fieldname', ...] — strip the
        // 'body' / 'query' / 'path' prefix the operator doesn't care
        // about, leaving just the field path.
        const loc = Array.isArray(d.loc)
          ? d.loc.filter((p) => p !== 'body' && p !== 'query' && p !== 'path').join('.')
          : '';
        return loc ? `${loc}: ${d.msg}` : d.msg;
      }
      return JSON.stringify(d);
    });
    return parts.join(' · ') || fallback;
  }

  // Detail is some other object — last-resort flatten
  if (detail && typeof detail === 'object') {
    if (detail.message) return String(detail.message);
    try { return JSON.stringify(detail); } catch (_e) { /* fall through */ }
  }

  // Network / programmatic Error
  if (error?.message) return error.message;
  return fallback;
}
